/*
  Files tab pieces (UI overhaul wave 1, screen 06):
  - FilesToolbar    — connection pill + ⋯ (Reconnect / Disconnect) + ONE navy "Scan OneDrive" (idea 2)
  - FolderPathCard  — the OneDrive folder as breadcrumb chips; "Change" opens the editor on purpose (idea 3)
  - FolderChecklist — wave 2 (idea 1): the 25 required folders, always visible, with their scan status
  Wave 3: "Last scanned … · by …" in FolderPathCard (idea 4; the nightly state is in the Scan history footer, idea 8); the stage each main folder
  proves as a chip on its row (idea 5); a row can be selected to preview its files (idea 7); the toolbar says
  "OneDrive is not set up" instead of offering a Connect that cannot work (FilesToolbar `onedriveOn`).
*/
import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Clock, Cloud, CloudOff, Ellipsis, ExternalLink, Folder, FolderCheck, FolderX, Link2, Loader2, Pencil, RefreshCw, Unplug } from 'lucide-react';
import { NotConfigured } from '@/components/ui/not-configured';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusPill, ToneDot } from '@/components/ui/status-pill';
import { Segmented } from '@/components/ui/segmented';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { ActionMenu, Panel, TabHeader } from './ProjectUi';
import { splitFolderPath } from './projectDetailModel';
import { FOLDER_STATUS, filterFolderRows } from './filesModel';

export function OneDriveConnectionPill({ connected, account }) {
  return (
    <span
      className="inline-flex h-9 max-w-full items-center gap-2 rounded-full border border-line bg-surface px-3 text-[13px] shadow-card"
      data-testid="onedrive-connection-pill"
    >
      {connected ? <Cloud className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" /> : <CloudOff className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />}
      <ToneDot tone={connected ? 'green' : 'gray'} />
      <span className="font-semibold text-ink">OneDrive</span>
      <span className="min-w-0 truncate text-ink-3">{connected ? (account || 'Connected') : 'Not connected'}</span>
    </span>
  );
}

export function FilesToolbar({
  subtitle,
  connected,
  account,
  authLoading,
  scanning,
  hasPath,
  onConnect,
  onDisconnect,
  onScan,
  onedriveOn = true,
  // 28 #8: false for a role whose Files module is View only — no Connect / Reconnect / Disconnect (the scan reads)
  canEdit = true,
}) {
  if (!onedriveOn) {
    return (
      <TabHeader
        title="Files"
        subtitle={subtitle}
        actions={<NotConfigured feature="onedrive" layout="inline" testId="files-onedrive-not-configured" />}
      />
    );
  }
  return (
    <TabHeader
      title="Files"
      subtitle={subtitle}
      actions={(
        <>
          <OneDriveConnectionPill connected={connected} account={account} />
          {connected && canEdit ? (
            <ActionMenu
              label="OneDrive connection"
              trigger={(
                <Button type="button" variant="outline" size="icon" aria-label="OneDrive connection options" data-testid="onedrive-menu">
                  <Ellipsis aria-hidden="true" />
                </Button>
              )}
              items={[
                { label: 'Reconnect OneDrive', icon: Link2, onSelect: onConnect, description: 'Sign in again, or with another account', testId: 'onedrive-reconnect' },
                { label: 'Disconnect…', icon: Unplug, onSelect: onDisconnect, danger: true, separatorBefore: true, testId: 'onedrive-disconnect' },
              ]}
            />
          ) : null}
          {connected ? (
            <Button
              type="button"
              onClick={onScan}
              disabled={scanning || !hasPath}
              title={hasPath ? undefined : 'Set the project folder first'}
              data-testid="files-scan"
            >
              {scanning ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
              {scanning ? 'Scanning…' : 'Scan OneDrive'}
            </Button>
          ) : canEdit ? (
            <Button type="button" onClick={onConnect} disabled={authLoading} data-testid="onedrive-connect">
              {authLoading ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Link2 aria-hidden="true" />}
              Connect OneDrive
            </Button>
          ) : null}
        </>
      )}
    />
  );
}

export function FolderPathCard({ path, onSave, saving, scannedAt, scannedLabel, scannedBy, canEdit = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(path || '');
  const inputRef = useRef(null);
  const parts = splitFolderPath(path);

  useEffect(() => {
    if (!editing) setDraft(path || '');
  }, [editing, path]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const save = async () => {
    const ok = await onSave?.(draft.trim());
    if (ok !== false) setEditing(false);
  };

  return (
    <Panel className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 sm:px-5" data-testid="files-folder-path">
      <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Project folder</span>
      {editing && canEdit ? (
        <form
          className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setEditing(false);
              }
            }}
            placeholder="Projects\\Project name\\System"
            aria-label="OneDrive folder path"
            className="h-9 min-w-[240px] flex-1"
            data-testid="files-folder-path-input"
          />
          <Button type="submit" size="sm" disabled={saving} data-testid="files-folder-path-save">
            {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            Save folder
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          <p className="basis-full text-[12px] text-ink-3">Path inside your OneDrive, e.g. Projects\35. LIV Waterside\HA. Press Esc to cancel.</p>
        </form>
      ) : (
        <>
          {parts.length ? (
            <ol className="flex min-w-0 flex-wrap items-center gap-1" aria-label="Project folder path">
              {parts.map((part, index) => (
                <li key={`${part}-${index}`} className="flex items-center gap-1">
                  {index > 0 ? <ChevronRight className="h-3.5 w-3.5 text-ink-4" aria-hidden="true" /> : null}
                  <span
                    className={cn(
                      'inline-flex h-7 items-center gap-1.5 rounded-lg border px-2 text-[12.5px]',
                      index === parts.length - 1 ? 'border-brand-soft-border bg-brand-soft font-semibold text-[color:var(--brand-ink)]' : 'border-line bg-surface-2 text-ink-2'
                    )}
                  >
                    <Folder className="h-3.5 w-3.5" aria-hidden="true" />
                    {part}
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <span className="text-[13px] text-ink-3">No folder set yet</span>
          )}
          {canEdit ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="files-folder-path-change">
              <Pencil aria-hidden="true" />
              {parts.length ? 'Change' : 'Set folder'}
            </Button>
          ) : null}
          <span className="ml-auto inline-flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-ink-3" title={scannedAt ? formatDateTime(scannedAt) : undefined} data-testid="files-last-scanned">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {scannedAt ? <>Last scanned <span className="font-semibold text-ink-2">{formatDateTime(scannedAt, { relativeDay: true, year: 'auto' })}</span></> : (scannedLabel || 'Not scanned yet')}
            {scannedAt && scannedBy ? <span>· by {scannedBy}</span> : null}
          </span>
        </>
      )}
    </Panel>
  );
}

/* ---------------------------------------------------------------- wave 2 (screen 06 idea 1) */

const FOLDER_ICON = { ready: FolderCheck, empty: Folder, missing: FolderX, not_scanned: Folder };
const FOLDER_ICON_CLASS = {
  ready: 'text-status-green',
  empty: 'text-status-amber',
  missing: 'text-status-red',
  not_scanned: 'text-ink-4',
};

/**
 * Required folders, always visible (06 #1): 9 groups / 25 folders, each with Ready / Empty / Missing /
 * Not scanned, its file count and a link to open it in OneDrive. "Needs attention" shows only the empty
 * and missing ones (with their group).
 */
export function FolderChecklist({ checklist, onOpenFolder, scanning = false, className, stageChipFor, selectedPath, onSelect, onedriveOn = true }) {
  const [filter, setFilter] = useState('all');
  const { rows, counts, scanned } = checklist;
  const shown = scanned ? filterFolderRows(rows, filter) : rows;
  return (
    <Panel className={cn('overflow-hidden', className)} data-testid="folder-checklist">
      <div className="flex min-h-[56px] flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-2.5 sm:px-5">
        <div className="flex min-w-0 items-baseline gap-2">
          <h3 className="text-[14px] font-bold text-ink">Required folders</h3>
          <span className="text-[12px] text-ink-3">
            {scanned ? `${counts.ready} of ${counts.all} ready` : `${counts.all} folders · not scanned yet`}
          </span>
        </div>
        {scanned ? (
          <Segmented
            size="sm"
            ariaLabel="Which folders"
            value={filter}
            onChange={setFilter}
            options={[
              { key: 'all', label: 'All', count: counts.all },
              { key: 'attention', label: 'Needs attention', count: counts.attention, dot: counts.attention ? 'amber' : undefined },
              { key: 'ready', label: 'Ready', count: counts.ready },
            ]}
            testId="folder-filter"
          />
        ) : (
          <span className="text-[12.5px] text-ink-3">{scanning ? 'Scanning OneDrive…' : onedriveOn ? 'Statuses appear after Scan OneDrive' : 'Statuses appear once OneDrive is set up'}</span>
        )}
      </div>
      <ul>
        {shown.map((row) => {
          const Icon = FOLDER_ICON[row.status] || Folder;
          const meta = FOLDER_STATUS[row.status];
          const groupSummary = row.isGroup && row.summary && row.status !== 'missing' && scanned
            ? [
              `${row.summary.folders} folder${row.summary.folders === 1 ? '' : 's'}`,
              row.summary.empty ? `${row.summary.empty} empty` : null,
              row.summary.missing ? `${row.summary.missing} missing` : null,
            ].filter(Boolean)
            : null;
          const chip = row.isGroup ? stageChipFor?.(row) : null;
          const selected = selectedPath === row.path;
          const missing = row.status === 'missing';
          return (
            <li
              key={row.id}
              className={cn(
                'relative flex min-h-[40px] items-center gap-2.5 border-b border-line-2 pr-4 last:border-b-0 sm:pr-5',
                row.isGroup ? 'min-h-[42px] bg-surface-2 pl-4 sm:pl-5' : '',
                onSelect && 'cursor-pointer',
                onSelect && !selected && !missing && 'hover:bg-surface-hover',
                // plain tokens: Tailwind cannot apply /NN opacity to colours defined as var(--x)
                missing && 'bg-status-red-bg',
                selected && 'bg-brand-soft before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-[color:var(--brand)]',
              )}
              style={row.isGroup ? undefined : { paddingLeft: 20 + row.depth * 24 }}
              data-testid={`folder-row-${row.id}`}
              data-status={row.status}
              aria-current={selected ? 'true' : undefined}
              onClick={onSelect ? (event) => {
                if (event.target.closest('button, a')) return; // the name button selects on its own
                onSelect(row);
              } : undefined}
            >
              <Icon className={cn('h-4 w-4 shrink-0', FOLDER_ICON_CLASS[row.status])} aria-hidden="true" />
              {/* phones: the name may take two lines and the stage chip wraps under it */}
              <span className={cn('flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 py-1.5 text-[13px] sm:flex-nowrap sm:py-0', row.isGroup ? 'font-bold text-ink' : 'text-ink', missing && 'text-status-red-ink')}>
                {onSelect ? (
                  <button
                    type="button"
                    data-folder-select
                    onClick={() => onSelect(row)}
                    className={cn('line-clamp-2 min-w-0 break-words rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:line-clamp-1', selected && 'text-[color:var(--brand-ink)]')}
                    title={row.reason || row.path}
                    aria-label={`${row.name}: ${meta.label}. Show files`}
                  >
                    {row.name}
                  </button>
                ) : <span className="line-clamp-2 min-w-0 break-words sm:line-clamp-1" title={row.reason || row.path}>{row.name}</span>}
                {chip}
              </span>
              {groupSummary ? (
                <span className="shrink-0 text-[12px] text-ink-3">
                  {groupSummary.map((part, index) => (
                    // On phones the folder count gives way to the group name; what needs attention stays.
                    <span key={part} className={cn(index === 0 && groupSummary.length > 1 && 'hidden sm:inline', index > 0 && (part.includes('missing') ? 'font-semibold text-status-red-ink' : 'font-semibold text-status-amber-ink'))}>
                      {index > 1 ? ' · ' : index === 1 ? <span className="hidden sm:inline"> · </span> : ''}{part}
                    </span>
                  ))}
                </span>
              ) : (
                <>
                  {/* phones: the dot alone (the name button says the status), so the name keeps the row */}
                  <ToneDot tone={meta.tone} className="shrink-0 sm:hidden" title={meta.label} />
                  <StatusPill tone={meta.tone} label={meta.label} size="sm" className="hidden shrink-0 sm:inline-flex" />
                  <span className="w-[24px] shrink-0 text-right text-[12px] tabular-nums text-ink-3 sm:w-[64px]" title={row.fileCount === null ? undefined : `${row.fileCount} file${row.fileCount === 1 ? '' : 's'}`}>
                    {row.fileCount === null ? '—' : <>{row.fileCount}<span className="hidden sm:inline"> file{row.fileCount === 1 ? '' : 's'}</span></>}
                  </span>
                </>
              )}
              {row.webUrl ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => onOpenFolder?.(row.webUrl)}
                  aria-label={`Open ${row.name} in OneDrive`}
                  title="Open in OneDrive"
                  className="shrink-0 text-ink-3"
                >
                  <ExternalLink aria-hidden="true" />
                </Button>
              ) : <span className="w-7 shrink-0" aria-hidden="true" />}
            </li>
          );
        })}
        {!shown.length ? (
          <li className="px-5 py-8 text-center text-[13px] text-ink-3">
            {filter === 'attention' ? 'Every required folder has files.' : 'No folder is ready yet.'}
          </li>
        ) : null}
      </ul>
    </Panel>
  );
}
